import * as React from 'react';
import { H5, Text } from '@shared/ui-components';

import { PaymentConstants } from 'common/constants/payment';
import { BillingPeriodUnit } from '../../../enums/billing-period-unit';
import { BillingPricingModel } from '../../../enums/billing-pricing-model';
import { SubscriptionAddon } from '../../../interfaces/subscription-addon';
import { SubscriptionDetailsModel } from '../../../interfaces/subscription-details';
import { BillingEstimationUtils } from '../../../utils/billing-estimation-utils';
import { Styled } from './styled';

interface Props {
  currentSubscription: SubscriptionDetailsModel;
  addons: SubscriptionAddon[];
  isViewMode?: boolean;
  extraCurrency?: string;
}


export class SubscriptionPrice extends React.Component<Props> {
  public render(): JSX.Element {
    const variant = this.props.currentSubscription.planVariant;
    const isPromo = variant.id.includes('sketchup');

    if (isPromo) {
      const price = this.props.currentSubscription.planVariant.price;

      return (
        <Styled.Container isViewMode={this.props.isViewMode}>
          <H5>{variant.name}</H5>
          <Styled.Price>
            <Styled.PriceCost>
              <Styled.PrimaryPrice>
                {this.getPriceString(price)}
              </Styled.PrimaryPrice>
              {this.props.extraCurrency && (
                <Styled.ExtraPrice>
                  {this.getExtraPrice(price, this.props.extraCurrency)}
                </Styled.ExtraPrice>
              )}
            </Styled.PriceCost>
            <Styled.PriceDescription>
              <Text fontSize={12} color='additionalFont'>
                {variant.pricingModel === BillingPricingModel.PerUnit
                  ? `per user, per ${variant.billingPeriodUnit}`
                  : `per ${variant.billingPeriodUnit}`}
              </Text>
            </Styled.PriceDescription>
          </Styled.Price>
        </Styled.Container>
      );
    }

    const price = this.getPrice();

    return (
      <Styled.Container isViewMode={this.props.isViewMode}>
        <H5>{variant.name}</H5>
        <Styled.Price>
          <Styled.PriceCost>
            <Styled.PrimaryPrice>
              {this.getPriceString(price)}
            </Styled.PrimaryPrice>
            {this.props.extraCurrency && (
              <Styled.ExtraPrice>
                {this.getExtraPrice(price, this.props.extraCurrency)}
              </Styled.ExtraPrice>
            )}
          </Styled.PriceCost>
          <Styled.PriceDescription>
            <Text fontSize={12} color='additionalFont'>
              {variant.pricingModel === BillingPricingModel.PerUnit
                ? 'per user, per month'
                : 'per month'}
            </Text>
            {
              variant.billingPeriodUnit === BillingPeriodUnit.Year && (
                <Text fontSize={12} color='additionalFont'>{'(paid annually)'}</Text>
              )
            }
          </Styled.PriceDescription>
        </Styled.Price>
      </Styled.Container>
    );
  }

  private getMonthCount(period: BillingPeriodUnit): number {
    switch (period) {
      case BillingPeriodUnit.Month: return 1;
      case BillingPeriodUnit.Year: return 12;
      default: return 1;
    }
  }

  private getPrice(): number {
    const { addons, currentSubscription } = this.props;

    const addonsPrice = addons
      .filter(x => currentSubscription.selectedAddonIds.includes(x.id))
      .reduce(((acc, addon) => acc + addon.price), 0);
    const monthCount = this.getMonthCount(currentSubscription.billingPeriod);
    return Math.round((currentSubscription.planVariant.price + addonsPrice) / monthCount);
  }

  private getPriceString(price: number): string {
    return `${PaymentConstants.defaultCurrency} ${price / 100}`;
  }

  private getExtraPrice(price: number, currency: string): string {
    const value = BillingEstimationUtils.getExtraCurrencyValue(price, currency);
    return `≈ ${currency}${value / 100}`;
  }
}
